/**
 * Growth, the activation slice: the funnel per surface and the a-ha analysis,
 * read from GET /provider/admin/growth/activation (pv-auth
 * services/growth/activation.*). Types mirror the payload; the helpers here
 * decide how a number is printed, and the rule is the server's: a rate from
 * too few people is printed as a count with the words "not enough data",
 * never as a percent.
 */
import { NOT_MEASURED } from './format';

export const MILESTONES = ['verified', 'first_check', 'reminder_set', 'second_day_check', 'active_or_calendar', 'converted', 'paid'] as const;
export type Milestone = (typeof MILESTONES)[number];

export interface SurfaceStep {
  milestone: Milestone;
  label: string;
  reached: number;
  pctOfCohort: number | null;
  dropped: number | null;
  droppedPctOfPrevious: number | null;
}

export interface SurfaceFunnelRow {
  surface: string;
  label: string;
  cohort: number;
  small: boolean;
  steps: SurfaceStep[];
  worst: { milestone: Milestone; label: string; dropped: number } | null;
}

export interface SurfaceFunnelWindow {
  days: number;
  since: string;
  cohort: number;
  rows: SurfaceFunnelRow[];
}

export interface ActionStat {
  action: string;
  label: string;
  withN: number;
  withRetained: number;
  withoutN: number;
  withoutRetained: number;
  rateWith: number | null;
  rateWithout: number | null;
  liftPoints: number | null;
  precision: number | null;
  recall: number | null;
  f1: number | null;
  z: number | null;
  enough: boolean;
}

export interface AhaOutcome {
  day: 7 | 30;
  eligible: number;
  retained: number;
  baseRate: number | null;
  enough: boolean;
  actions: ActionStat[];
  candidate: ActionStat | null;
  note: string;
}

export interface ActivationPayload {
  generatedAt: string;
  people: number;
  excluded: { qa: number; team: number };
  surfaces: SurfaceFunnelWindow[];
  aha: { d30: AhaOutcome; d7: AhaOutcome };
  unavailable: { source: string; reason: string }[];
}

export async function fetchActivation(): Promise<ActivationPayload> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet('/provider/admin/growth/activation', true)) as ActivationPayload;
}

/* ── Pure helpers ───────────────────────────────────────────────────────── */

export const NOT_ENOUGH = 'not enough data';

/** "12 (40%)" for a surface big enough, "3" for a small one, "0" when nobody; the percent is withheld on a small cohort. */
export const reachedCell = (step: SurfaceStep, small: boolean): string => {
  if (small || step.pctOfCohort === null) return String(step.reached);
  return `${step.reached} (${step.pctOfCohort}%)`;
};

/** The lost-between-steps line: "4 lost (40%)", "nobody lost", or blank for the first step. On a small cohort the percent is withheld. */
export const droppedCell = (step: SurfaceStep, small: boolean): string => {
  if (step.dropped === null) return '';
  if (step.dropped === 0) return 'nobody lost';
  if (small || step.droppedPctOfPrevious === null) return `${step.dropped} lost`;
  return `${step.dropped} lost (${step.droppedPctOfPrevious}%)`;
};

/** The retained-of line for one side of an action: "14 of 20 (70%)", or "3 of 4" with the percent withheld. */
export const sideCell = (retained: number, n: number, enough: boolean): string => {
  if (n === 0) return 'nobody';
  const base = `${retained} of ${n}`;
  return enough ? `${base} (${Math.round((retained / n) * 100)}%)` : base;
};

/** The lift column: "+60 pts" when both sides are big enough, else the words. */
export const liftCell = (s: ActionStat): string => {
  if (!s.enough || s.liftPoints === null) return NOT_ENOUGH;
  const sign = s.liftPoints > 0 ? '+' : '';
  return `${sign}${s.liftPoints} pts`;
};

/** "likely real" when the z score clears 1.96 on enough people, "could be chance" otherwise; blank without a lift. */
export const confidenceCell = (s: ActionStat): string => {
  if (!s.enough || s.z === null) return '';
  return Math.abs(s.z) >= 1.96 ? 'likely real' : 'could be chance';
};

/** A rate as a percent, or the words when null. */
export const ratePct = (rate: number | null): string => (rate === null ? NOT_MEASURED : `${Math.round(rate * 100)}%`);

/** Actions sorted for reading: the ones with a lift first (largest first), then by how many took them. */
export const sortActions = (actions: ActionStat[]): ActionStat[] =>
  [...actions].sort((a, b) => {
    const la = a.enough && a.liftPoints !== null ? a.liftPoints : null;
    const lb = b.enough && b.liftPoints !== null ? b.liftPoints : null;
    if (la !== null && lb !== null) return lb - la;
    if (la !== null) return -1;
    if (lb !== null) return 1;
    return b.withN - a.withN;
  });

/** The biggest loss point across the window's surfaces, by people lost; null when nobody was lost anywhere. */
export const worstSurfaceStep = (w: SurfaceFunnelWindow): { surface: string; label: string; step: string; dropped: number } | null => {
  let best: { surface: string; label: string; step: string; dropped: number } | null = null;
  for (const r of w.rows) {
    if (r.surface === 'all' || !r.worst) continue;
    if (!best || r.worst.dropped > best.dropped) best = { surface: r.surface, label: r.label, step: r.worst.label, dropped: r.worst.dropped };
  }
  return best;
};
